/**
 * Control panel. Values are SI internally; the panel converts for display
 * (hPa, km). Each change mutates the shared ControlState and then calls
 * onChange with the key that changed.
 */
import type { TierId } from '../config/tiers';
import { TIER_ORDER } from '../config/tiers';
import type { CowMode } from '../physics/rigidBodyDrag';

export interface ControlState {
  cowMode: CowMode;
  deltaPPa: number;
  rMaxM: number;
  B: number;
  latitudeDeg: number;
  timeScale: number;
  /** Display only: multiplies rendered heights. */
  verticalStretch: number;
  steerMps: number;
  steerHeadingDeg: number;
  inflowPeakMps: number;
  turbulenceMps: number;
  debugOverlay: boolean;
  clouds: boolean;
  hudVisible: boolean;
  paused: boolean;
  /** 'auto' lets the governor decide. Any tier id disables the governor. */
  tierOverride: TierId | 'auto';
}

export type ControlKey = keyof ControlState;

export interface ControlsHandle {
  /** Push state back into the widgets (e.g. after the governor changes tier). */
  sync(): void;
}

interface SliderSpec {
  key:
    | 'deltaPPa'
    | 'rMaxM'
    | 'B'
    | 'latitudeDeg'
    | 'timeScale'
    | 'verticalStretch'
    | 'steerMps'
    | 'steerHeadingDeg'
    | 'inflowPeakMps'
    | 'turbulenceMps';
  label: string;
  min: number;
  max: number;
  step: number;
  /** SI value to display string. */
  show(v: number): string;
}

const SLIDERS: readonly SliderSpec[] = [
  {
    key: 'deltaPPa',
    label: 'Δp',
    min: 1000,
    max: 10000,
    step: 100,
    show: (v) => `${(v / 100).toFixed(0)} hPa`,
  },
  {
    key: 'rMaxM',
    label: 'Rmax',
    min: 10000,
    max: 80000,
    step: 1000,
    show: (v) => `${(v / 1000).toFixed(0)} km`,
  },
  { key: 'B', label: 'Holland B', min: 1, max: 2.5, step: 0.05, show: (v) => v.toFixed(2) },
  {
    key: 'latitudeDeg',
    label: 'Latitude',
    min: -45,
    max: 45,
    step: 1,
    show: (v) => `${v.toFixed(0)}°`,
  },
  {
    key: 'timeScale',
    label: 'Time scale',
    min: 10,
    max: 600,
    step: 10,
    show: (v) => `${v.toFixed(0)}×`,
  },
  {
    key: 'verticalStretch',
    label: 'Height ×',
    min: 1,
    max: 10,
    step: 1,
    show: (v) => `${v.toFixed(0)}× view`,
  },
  {
    key: 'steerMps',
    label: 'Steering',
    min: 0,
    max: 15,
    step: 0.5,
    show: (v) => `${v.toFixed(1)} m/s`,
  },
  {
    key: 'steerHeadingDeg',
    label: 'Heading',
    min: 0,
    max: 355,
    step: 5,
    show: (v) => `${v.toFixed(0)}°`,
  },
  {
    key: 'inflowPeakMps',
    label: 'Inflow',
    min: 0,
    max: 30,
    step: 1,
    show: (v) => `${v.toFixed(0)} m/s`,
  },
  {
    key: 'turbulenceMps',
    label: 'Turbulence',
    min: 0,
    max: 20,
    step: 1,
    show: (v) => `${v.toFixed(0)} m/s`,
  },
];

const COW_MODES: readonly { mode: CowMode; label: string; enabled: boolean }[] = [
  { mode: 'spherical', label: 'Spherical', enabled: true },
  { mode: 'vacuum', label: 'Vacuum', enabled: true },
  { mode: 'realistic', label: 'Realistic (tumbling ellipsoid)', enabled: true },
];

export function createControls(
  root: HTMLElement,
  state: ControlState,
  onChange: (key: ControlKey) => void,
): ControlsHandle {
  root.classList.add('controls');
  const syncers: (() => void)[] = [];

  const title = document.createElement('div');
  title.className = 'controls-title';
  title.textContent = 'Holland (1980) vortex';
  root.appendChild(title);
  const lab = document.createElement('a');
  lab.href = './lab.html';
  lab.className = 'controls-lab-link';
  lab.textContent = 'Open the Lab: one chart per model →';
  root.appendChild(lab);

  for (const s of SLIDERS) {
    const row = document.createElement('label');
    row.className = 'controls-row';
    const name = document.createElement('span');
    name.textContent = s.label;
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(s.min);
    input.max = String(s.max);
    input.step = String(s.step);
    const out = document.createElement('output');
    row.append(name, input, out);
    root.appendChild(row);
    const sync = (): void => {
      input.value = String(state[s.key]);
      out.textContent = s.show(state[s.key]);
    };
    input.addEventListener('input', () => {
      state[s.key] = Number(input.value);
      out.textContent = s.show(state[s.key]);
      onChange(s.key);
    });
    syncers.push(sync);
  }

  const cowFs = document.createElement('fieldset');
  cowFs.className = 'controls-group';
  const legend = document.createElement('legend');
  legend.textContent = 'Cow';
  cowFs.appendChild(legend);
  for (const c of COW_MODES) {
    const lab = document.createElement('label');
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'cow-mode';
    radio.value = c.mode;
    radio.disabled = !c.enabled;
    lab.append(radio, ` ${c.label}${c.enabled ? '' : ' (phase 6)'}`);
    radio.addEventListener('change', () => {
      if (radio.checked) {
        state.cowMode = c.mode;
        onChange('cowMode');
      }
    });
    syncers.push(() => {
      radio.checked = state.cowMode === c.mode;
    });
    cowFs.appendChild(lab);
  }
  root.appendChild(cowFs);

  const toggles: readonly {
    key: 'debugOverlay' | 'clouds' | 'hudVisible' | 'paused';
    label: string;
  }[] = [
    { key: 'debugOverlay', label: 'Debug overlay' },
    { key: 'clouds', label: 'Clouds' },
    { key: 'hudVisible', label: 'HUD' },
    { key: 'paused', label: 'Paused' },
  ];
  for (const t of toggles) {
    const lab = document.createElement('label');
    lab.className = 'controls-toggle';
    const box = document.createElement('input');
    box.type = 'checkbox';
    lab.append(box, ` ${t.label}`);
    box.addEventListener('change', () => {
      state[t.key] = box.checked;
      onChange(t.key);
    });
    syncers.push(() => {
      box.checked = state[t.key];
    });
    root.appendChild(lab);
  }

  const tierRow = document.createElement('label');
  tierRow.className = 'controls-row';
  const tierName = document.createElement('span');
  tierName.textContent = 'Tier';
  const select = document.createElement('select');
  for (const id of ['auto', ...TIER_ORDER] as const) {
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = id === 'auto' ? 'auto (governor)' : id;
    select.appendChild(opt);
  }
  select.addEventListener('change', () => {
    state.tierOverride = select.value as TierId | 'auto';
    onChange('tierOverride');
  });
  syncers.push(() => {
    select.value = state.tierOverride;
  });
  tierRow.append(tierName, select);
  root.appendChild(tierRow);

  const handle: ControlsHandle = {
    sync(): void {
      for (const s of syncers) s();
    },
  };
  handle.sync();
  return handle;
}
